import { useMemo } from 'react';
import { PARTNER_USER_ROLE_OPTIONS } from 'vopay';
import useUserRoleLabels from '@/hooks/useUserRoleLabels';
import type { RoleCount } from './RolesSummaryCard';

// TODO: UserType stays a plain string (not a narrowed 'Admin' | 'Support' |
// 'Viewer' union) because this is fed by AccountUser, which is shared with
// client-dashboard's dynamic/custom account roles — narrowing it here would
// be inaccurate for that caller. Revisit once partner portal gets its own
// roles endpoint/response type; the real fix belongs there, not as a local
// cast in this file.
type PartnerUser = { UserType: string };

// TODO: partner portal has no roles endpoint yet — roles are the fixed
// PARTNER_USER_ROLE_OPTIONS enum, and each user already carries its UserType
// from the users list we've already fetched. Tally client-side instead of
// making any additional request. Every option is always included (even at
// count 0) so the cards match the fixed role set, not just whichever roles
// happen to be in use.
//
// Once a real per-role-count endpoint exists, swap this hook for one that
// fetches instead of tallies — keep the same RoleCount[] output shape so
// RolesSummaryCard doesn't need to change.
export default function useComputePartnerRoleCounts(users: PartnerUser[]): RoleCount[] {
  const roleLabels = useUserRoleLabels();

  return useMemo(() => {
    const counts: Record<string, number> = {};

    for (const user of users) {
      counts[user.UserType] = (counts[user.UserType] ?? 0) + 1;
    }

    return Object.keys(PARTNER_USER_ROLE_OPTIONS).map((role) => ({
      id: role,
      name: roleLabels[role] ?? role,
      count: counts[role] ?? 0,
    }));
  }, [users, roleLabels]);
}
